import React, { useState, useEffect } from 'react';
import { Shield, Cookie, Check, X, Settings2, Lock, BarChart3, Target, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const COOKIE_STORAGE_KEY = 'aurahair_cookie_consent_v1';

export default function CookieConsentBanner({ onOpenPrivacy, isOpenCustomModal, onCloseCustomModal }) {
  const { lang } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Granular Cookie Preferences
  const [preferences, setPreferences] = useState({
    essential: true, // Always true & disabled
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_STORAGE_KEY);
      if (!stored) {
        // First visit: show bottom banner after a short delay
        const timer = setTimeout(() => setShowBanner(true), 800);
        return () => clearTimeout(timer);
      } else {
        const parsed = JSON.parse(stored);
        if (parsed) {
          setPreferences(parsed);
        }
      }
    } catch (e) {
      setShowBanner(true);
    }
  }, []);

  // Listen for external trigger from footer "Cookie-instellingen"
  useEffect(() => {
    if (isOpenCustomModal) {
      setIsModalOpen(true);
    }
  }, [isOpenCustomModal]);

  const saveConsent = (prefs) => {
    try {
      localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {}
    setPreferences(prefs);
    setShowBanner(false);
    setIsModalOpen(false);
    if (onCloseCustomModal) onCloseCustomModal();

    // Broadcast change and conditionally initialize tracking scripts
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('aurahair_cookie_consent_updated', { detail: prefs }));

      // Conditionally load Google Analytics / Pixel if user gave consent
      if (prefs?.analytics && window.AURAHAR_GA_ID) {
        if (!document.getElementById('aurahair-ga-script')) {
          const script = document.createElement('script');
          script.id = 'aurahair-ga-script';
          script.async = true;
          script.src = `https://www.googletagmanager.com/gtag/js?id=${window.AURAHAR_GA_ID}`;
          document.head.appendChild(script);
          window.dataLayer = window.dataLayer || [];
          function gtag(){ window.dataLayer.push(arguments); }
          gtag('js', new Date());
          gtag('config', window.AURAHAR_GA_ID, { anonymize_ip: true });
        }
      }
    }
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    });
  };

  const handleAcceptEssentialOnly = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    });
  };

  const handleSavePreferences = () => {
    saveConsent({
      ...preferences,
      essential: true,
      timestamp: new Date().toISOString()
    });
  };

  // Translations
  const content = {
    nl: {
      title: "Wij respecteren uw privacy",
      summary: "Aura Hair gebruikt functionele cookies om de website en het online reserveringssysteem goed te laten werken. Met uw toestemming gebruiken we ook analytische en marketingcookies om uw ervaring te verbeteren conform de AVG / Telecommunicatiewet.",
      acceptAll: "Alles Accepteren",
      essentialOnly: "Alleen Noodzakelijk",
      customize: "Voorkeuren Aanpassen",
      modalTitle: "Cookie-instellingen & Privacyvoorkeuren",
      modalDesc: "U kunt zelf bepalen welke categorieën cookies u toestaat. Noodzakelijke cookies kunnen niet worden uitgeschakeld.",
      essentialTitle: "Noodzakelijke Cookies",
      essentialDesc: "Vereist voor het functioneren van de website, winkelwagen, reserveringen en veilige PIN/iDEAL sessies.",
      alwaysActive: "Altijd Actief",
      analyticsTitle: "Analytische Cookies",
      analyticsDesc: "Helpen ons anoniem inzicht te krijgen in het bezoekersgedrag en de prestaties van onze salonpagina.",
      marketingTitle: "Marketing & Social Media",
      marketingDesc: "Gebruikt om relevante salonacties en advertenties te tonen via Google en sociale media.",
      saveBtn: "Voorkeuren Opslaan",
      privacyLink: "Lees onze volledige Privacyverklaring"
    },
    en: {
      title: "We value your privacy",
      summary: "Aura Hair uses functional cookies to ensure smooth bookings and site operations. With your consent, we also use analytical and marketing cookies in compliance with GDPR / Dutch AVG.",
      acceptAll: "Accept All",
      essentialOnly: "Essential Only",
      customize: "Customize Settings",
      modalTitle: "Cookie Settings & Privacy Preferences",
      modalDesc: "Select which cookie categories you allow. Essential cookies cannot be disabled.",
      essentialTitle: "Essential Cookies",
      essentialDesc: "Required for core site features, reservations, shopping cart and secure sessions.",
      alwaysActive: "Always Active",
      analyticsTitle: "Analytical Cookies",
      analyticsDesc: "Help us anonymously understand visitor interactions and optimize site speed.",
      marketingTitle: "Marketing & Social Media",
      marketingDesc: "Used to present relevant salon promotions and ads on social channels.",
      saveBtn: "Save Preferences",
      privacyLink: "Read our full Privacy Policy"
    },
    tr: {
      title: "Gizliliğinize Önem Veriyoruz",
      summary: "Aura Hair, randevu sisteminin ve sitenin güvenle çalışması için zorunlu çerezler kullanır. İzniniz doğrultusunda, deneyiminizi geliştirmek için analitik ve pazarlama çerezleri de kullanılmaktadır.",
      acceptAll: "Tümünü Kabul Et",
      essentialOnly: "Yalnızca Zorunlular",
      customize: "Tercihleri Özelleştir",
      modalTitle: "Çerez Ayarları & Gizlilik Tercihleri",
      modalDesc: "İzin verdiğiniz çerez kategorilerini belirleyebilirsiniz. Zorunlu çerezler kapatılamaz.",
      essentialTitle: "Zorunlu Çerezler",
      essentialDesc: "Sitenin çalışması, randevu oluşturma ve güvenli oturum yönetimi için zorunludur.",
      alwaysActive: "Her Zaman Aktif",
      analyticsTitle: "Analitik Çerezler",
      analyticsDesc: "Ziyaretçi trafiğini ve sayfa hızını anonim olarak analiz etmemize yardımcı olur.",
      marketingTitle: "Pazarlama ve Sosyal Medya",
      marketingDesc: "Sosyal medya ve arama motorlarında özel kampanya ve duyurular sunmak için kullanılır.",
      saveBtn: "Tercihlerimi Kaydet",
      privacyLink: "Gizlilik Politikamızı İnceleyin"
    }
  };

  const t = content[lang] || content.nl;

  return (
    <>
      {/* 🍪 1. Bottom Floating Cookie Consent Banner */}
      {showBanner && !isModalOpen && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3 sm:p-4 bg-white/98 backdrop-blur-xl border-t border-slate-200 text-slate-800 shadow-2xl animate-fade-in">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-4">
            
            {/* Left: Icon & Text */}
            <div className="flex items-start sm:items-center gap-3.5">
              <div className="w-10 h-10 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-800 shrink-0 shadow-xs">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="font-serif text-sm font-bold text-slate-900">{t.title}</div>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">
                    AVG / GDPR
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed max-w-3xl">
                  {t.summary}{' '}
                  {onOpenPrivacy && (
                    <button
                      type="button"
                      onClick={onOpenPrivacy}
                      className="text-amber-900 hover:text-amber-950 underline font-bold cursor-pointer inline-flex items-center gap-0.5 ml-1"
                    >
                      {t.privacyLink}
                    </button>
                  )}
                </p>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full lg:w-auto shrink-0 justify-end">
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="min-h-[44px] py-2.5 px-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 text-xs font-bold transition-all cursor-pointer border border-slate-200 flex items-center justify-center gap-1.5"
              >
                <Settings2 className="w-3.5 h-3.5 text-slate-500" />
                <span>{t.customize}</span>
              </button>

              <button
                type="button"
                onClick={handleAcceptEssentialOnly}
                className="min-h-[44px] py-2.5 px-3.5 rounded-xl bg-slate-100 hover:bg-rose-50 text-slate-700 hover:text-rose-700 text-xs font-bold transition-all cursor-pointer border border-slate-200 hover:border-rose-300 flex items-center justify-center"
              >
                {t.essentialOnly}
              </button>

              <button
                type="button"
                onClick={handleAcceptAll}
                className="min-h-[44px] py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 hover:from-amber-600 hover:to-amber-700 text-white font-black text-xs uppercase tracking-wider transition-all cursor-pointer shadow-md shadow-amber-500/25 flex items-center justify-center gap-1.5 hover:scale-[1.02]"
              >
                <Check className="w-4 h-4 stroke-[3]" />
                <span>{t.acceptAll}</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ⚙️ 2. Cookie Granular Preferences Modal */}
      {isModalOpen && (
        <div 
          onClick={() => {
            setIsModalOpen(false);
            if (onCloseCustomModal) onCloseCustomModal();
          }}
          className="fixed inset-0 z-[99995] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto animate-fade-in"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-900/98 border-2 border-slate-800 p-6 sm:p-7 rounded-3xl w-full max-w-xl space-y-5 text-white shadow-2xl relative my-auto text-left"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shadow-md">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-white">{t.modalTitle}</h3>
                  <p className="text-xs text-slate-400">{t.modalDesc}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  if (onCloseCustomModal) onCloseCustomModal();
                }}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cookie Categories */}
            <div className="space-y-3">
              
              {/* Essential Category */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <div className="space-y-0.5">
                    <div className="text-sm font-bold text-white flex items-center gap-2">
                      <span>{t.essentialTitle}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t.essentialDesc}
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-800 text-emerald-400 shrink-0 border border-slate-700">
                  {t.alwaysActive}
                </span>
              </div>

              {/* Analytics Category */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <BarChart3 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                  <div className="space-y-0.5">
                    <div className="text-sm font-bold text-white flex items-center gap-2">
                      <span>{t.analyticsTitle}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t.analyticsDesc}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>

              {/* Marketing Category */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
                  <div className="space-y-0.5">
                    <div className="text-sm font-bold text-white flex items-center gap-2">
                      <span>{t.marketingTitle}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t.marketingDesc}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>

            </div>

            {/* Modal Actions */}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={handleAcceptEssentialOnly}
                className="flex-1 py-3 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-colors cursor-pointer border border-slate-700 text-center"
              >
                {t.essentialOnly}
              </button>
              <button
                type="button"
                onClick={handleSavePreferences}
                className="flex-[2] py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg shadow-amber-500/25 flex items-center justify-center gap-1.5"
              >
                <Check className="w-4 h-4 stroke-[3]" />
                <span>{t.saveBtn}</span>
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}
